/**
 * @author MR
 * @date   2025-02-28
 * @desc   日期时间显示器（自动更新）
 * @requires moment 本插件依赖库
 */

import moment from "moment/moment";

/**
 * 日期时间显示器
 * @param opt
 * @param {Number} opt.timestamp 显示时间戳，可选参数。默认取当前系统时间。
 * @param {Number} opt.template 时间字符串模板字符串，可选参数。
 * @param {Function} opt.render 时间字符串渲染方法，可选参数。
 * @param {Function} opt.callback 每间隔 1s 循环执行的回调，可选参数。
 * @param {Boolean} opt.local 是否使用本地时间，可选参数。
 * @param {Boolean} opt.autoStart 是否自动启动，可选参数。
 * @param {Number} opt.startDeviation 开始时偏差校准时间，单位为 ms，可选参数。
 * @constructor
 */
export function DateTimeDisplay(opt) {
  if (!(this instanceof DateTimeDisplay)) {
    throw new Error('DateTimeDisplay 调用方式有误！')
  }

	var defaultTemplate = '{year}年{month}月{day}日 {hour}:{minute}:{second}';
	var option = opt || {};
	this.timestamp = option.timestamp || +new Date();
	this.template = option.template || defaultTemplate;
	this.render = typeof option.render === 'function' ? option.render : null;
	this.callback = typeof option.callback === 'function' ? option.callback : null;
	this.local = !!option.local;

	this.timer = null; // 定时器
	this.dateTime = {}; // 时间对象：年月日星期时分秒时间戳
	this.dateTimeStr = ''; // 时间字符串
	this.startTime = 0;
	this.startDeviation = option.startDeviation || 0;

	this.start = function (timestamp, startDeviation) {
		// console.log('start', timestamp)
		this.startTime = +new Date();
		if (this.local) {
			this.timestamp = +new Date();
		} else {
			var ts = timestamp ? timestamp : this.timestamp ? this.timestamp : +new Date();
			this.timestamp = Number(moment(ts).format('x'));
		}
		this.timestamp += (startDeviation || this.startDeviation);
		// console.log('after startDeviation', this.timestamp)

		this.setInterval();
		this.run();
	}

  this.setInterval = function() {
    var _this = this;
    var inner = function () {
			var elapsed = +new Date() - _this.startTime;
			_this.timestamp += elapsed;
			_this.startTime = +new Date();
			_this.run.call(_this);
      _this.timer = setTimeout(inner, _this.getIntervalDelay());
    }
    _this.timer = setTimeout(inner, this.getIntervalDelay());
  }

  this.getIntervalDelay = function() {
    return 1000 - moment().valueOf() % 1000;
  }

	this.stop = function () {
		if (this.timer) clearTimeout(this.timer);
		this.timer = null;
	}

	this.run = function() {
		this.dateTime = this.getDateObj(this.timestamp);
		this.dateTimeStr = this.render
			? this.render(this.dateTime)
			: this.templateReplace(this.template, this.dateTime);
		if (this.callback) this.callback(this.dateTimeStr, this.timestamp);
	};

	// 获取日期时间对象
	this.getDateObj = function(timestamp) {
		var date = moment(timestamp);
		var weekChMap = { '1': '一', '2': '二', '3': '三', '4': '四', '5': '五', '6': '六', '7': '日' };
		var dateObj = {
			year: date.format('YYYY'),
			month: date.format('MM'),
			day: date.format('DD'),
			hour: date.format('HH'),
			minute: date.format('mm'),
			second: date.format('ss'),
			week: date.format('E'),
			weekCh: weekChMap[date.format('E')],
			timestamp: timestamp
		}
		return dateObj;
	}

	this.templateReplace = function (template, data) {
		return template.replace(/{(\w+)}/g, function (match, key) {
			return data[key] !== undefined? data[key] : match;
		});
	}

	option.autoStart && this.start(this.timestamp);
}

// 监听网页窗口状态
export function listenVisibilityChange(visibleFunc, hiddenFunc) {
	// 检查浏览器是否支持 visibilitychange 事件
    if (typeof document.hidden !== "undefined") {
    	var visibilityChange = "visibilitychange";
    } else if (typeof document.msHidden !== "undefined") {
        var visibilityChange = "msvisibilitychange";
    } else if (typeof document.webkitHidden !== "undefined") {
        var visibilityChange = "webkitvisibilitychange";
    }

    // 定义处理页面可见性变化的函数
    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        console.log('网页已激活');
        // 在这里可以添加网页激活时要执行的代码
        typeof visibleFunc === 'function' && visibleFunc();
    	} else {
        console.log('网页已隐藏');
        // 在这里可以添加网页隐藏时要执行的代码
        typeof hiddenFunc === 'function' && hiddenFunc();
      }
    }

	document.addEventListener(visibilityChange, handleVisibilityChange, false);
}

/**
 * 精准无偏差定时器
 * @param callback 循环执行的方法
 * @param delay 循环间隔，时间单位 ms
 */
export function setPreciseInterval(callback, delay) {
	if (typeof callback !== 'function') return;
	if (!Number(delay) || Number(delay) < 0) return;

  var timer;
	var startTime = +new Date();

	var _this = this instanceof setPreciseInterval ? this : null;
	var inner = function () {
		var elapsed = +new Date() - startTime;
		var remaining = delay - elapsed;
		if (remaining <= 0) {
			callback();
			startTime = +new Date();
			timer = setTimeout(inner, delay);
		} else {
			timer = setTimeout(inner, remaining);
		}
    _this && (_this.timer = timer);
	}

	timer = setTimeout(inner, delay);
  _this && (_this.timer = timer);
}

/**
 * 精准无偏差定时器
 * @param callback 循环执行的方法
 * @param delay 循环间隔，时间单位 ms
 */
export function PreciseInterval(callback, delay) {
	if (typeof callback !== 'function') return;
	if (!Number(delay) || Number(delay) < 0) return;

	var _this = this instanceof PreciseInterval ? this : {};

	_this.isStart = false;
  _this.timer = null;
	_this.startTime = +new Date();

	const inner = function () {
		if (!_this.isStart) return;
		var elapsed = +new Date() - _this.startTime;
		var remaining = delay - elapsed;
		if (remaining <= 0) {
			callback();
			_this.startTime = +new Date();
			_this.timer = setTimeout(inner, delay);
		} else {
			_this.timer = setTimeout(inner, remaining);
		}
	}

	_this.start = function () {
		_this.isStart = true;
		_this.timer = setTimeout(inner, delay);
	}

	_this.stop = function () {
		_this.isStart = false;
		if (_this.timer) clearTimeout(_this.timer);
		_this.timer = null;
	}

	_this.start();
}